// @genesys-custom-file
import { round } from 'es-toolkit';
import * as React from 'react';

import { Input, type InputProps } from '@/components/ui/input';
import { UndoRedoActionType } from '@/editor/core/undoRedo/undoRedoTypes';
import { cn } from '@/lib/utils';

// Based on shadcn-react-number-scrubber

/**
 * Helper function to normalize input (replace comma with period)
 */
const normalizeInput = (input: string): string => {
  return input.replace(',', '.');
};

/**
 * Helper function to format display value with period as decimal separator
 */
const formatDisplayValue = (num: number | null | undefined, decimalPlaces?: number): string => {
  // Handle null/undefined values
  if (num === null || num === undefined || !Number.isFinite(num)) {
    return '0';
  }

  if (decimalPlaces !== undefined) {
    return round(num, decimalPlaces).toString();
  }

  return num.toString();
};

export interface InputNumberProps
  extends Omit<InputProps, 'onChange'> {
  /**
   * Current numeric value
   */
  value: number;
  /**
   * Callback whenever the value changes
   */
  onChange: (value: number, actionType?: UndoRedoActionType) => void;
  /**
   * Minimum allowable value (clamped)
   */
  min?: number;
  /**
   * Maximum allowable value (clamped)
   */
  max?: number;
  /**
   * Step for increments (e.g., 1, 0.1, etc.)
   */
  step?: number;
  /**
   * Number of decimal places to display/format the final value
   */
  decimalPlaces?: number;
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
  /**
   * Additional class names for the outer wrapper
   */
  className?: string;
  /**
   * Prefix label or content to display inside the input on the left
   */
  prefixLabel?: React.ReactNode;
  /**
   * Suffix label or content to display inside the input on the right
   */
  suffixLabel?: React.ReactNode;
  /**
   * Custom padding class when prefix is present (default: 'pl-6')
   */
  prefixPadding?: string;
  /**
   * Custom padding class when suffix is present (default: 'pr-1')
   */
  suffixPadding?: string;

  /**
   * Controls the number of pixels required to increase/decrease a step.
   * A value of 1.0 means 1 pixel per step; 0.1 means 10 pixels per step
   *
   * * For fine-grained control (like opacity: 0-1): use a small value like 0.001
   * * For medium control (like rotation: 0-360): use a medium value like 0.1
   * * For coarse control (like integer counts): use a larger value like 0.5
   */
  scrubSensitivity?: number;
  /**
   * Minimum pixels to move before starting drag mode
   */
  dragThreshold?: number;
}

export const InputNumber = React.forwardRef<
  HTMLInputElement,
  InputNumberProps
>(
  (
    {
      value,
      onChange,
      min,
      max,
      step = 0.01,
      decimalPlaces,
      disabled = false,
      className,
      prefixLabel,
      suffixLabel,
      prefixPadding,
      suffixPadding,
      scrubSensitivity = 0.5,
      dragThreshold = 3,
      onDragStart,
      ...rest
    },
    ref
  ) => {
    // Internal state
    const [internalValue, setInternalValue] = React.useState<number>(value ?? 0);
    const [displayValue, setDisplayValue] = React.useState<string>(value?.toString() ?? '0');
    const [isEmpty, setIsEmpty] = React.useState(false);
    const [isDragging, setIsDragging] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);
    const [previousValue, setPreviousValue] = React.useState<number>(value ?? 0);

    // Refs to track dragging and editing
    const isDraggingRef = React.useRef(false);
    const hasExceededThresholdRef = React.useRef(false);
    const preventFocusAfterDragRef = React.useRef(false);
    const initialXRef = React.useRef(0);
    const initialValueRef = React.useRef(value);
    const finalDragValueRef = React.useRef(value);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Determine how many decimals to keep for step quantization (drag operations)
    const stepDecimals = React.useMemo(() => {
      if (!Number.isFinite(step)) return 0;
      const stepString = step.toString();
      const decimalPart = stepString.split('.')[1];
      return decimalPart ? decimalPart.length : 0;
    }, [step]);

    /**
     * Helper function to commit a value change
     */
    const commitValue = React.useCallback((inputVal: string) => {
      // Normalize comma to period
      const normalizedInput = normalizeInput(inputVal);

      if (normalizedInput === '' || normalizedInput === '-' || normalizedInput === '.') {
        return;
      }

      const parsed = parseFloat(normalizedInput);
      if (isNaN(parsed)) {
        return;
      }

      // Clamp without step quantization
      const minValue = min ?? -Infinity;
      const maxValue = max ?? Infinity;
      const clamped = Math.max(minValue, Math.min(parsed, maxValue));
      // Apply final decimal places if specified
      let newValue = decimalPlaces !== undefined ? round(clamped, decimalPlaces) : clamped;

      // If value rounded to 0 but original was non-zero, clamp to minimum representable unit
      // This prevents typing 0.0001 with decimalPlaces=3 from becoming 0
      if (decimalPlaces !== undefined && newValue === 0 && Math.abs(parsed) > 0) {
        const minUnit = Math.pow(10, -decimalPlaces);
        newValue = parsed > 0 ? minUnit : -minUnit;
      }

      setInternalValue(newValue);
      setDisplayValue(formatDisplayValue(newValue, decimalPlaces));
      onChange(newValue, UndoRedoActionType.Primary); // Primary action for typing
    }, [min, max, decimalPlaces, onChange]);



    /**
     * On pointer down: start dragging
     */
    function handlePointerDown(e: React.PointerEvent) {
      // Only left-click and not disabled
      if (e.button !== 0 || disabled) return;

      isDraggingRef.current = true;
      hasExceededThresholdRef.current = false;
      initialXRef.current = e.clientX;
      initialValueRef.current = internalValue;
      finalDragValueRef.current = internalValue; // Initialize with starting value
      setPreviousValue(internalValue);
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    }

    const handleNativeDragStart = (e: React.DragEvent<HTMLInputElement>) => {
      e.preventDefault();
      onDragStart?.(e);
    };

    /**
     * On pointer move: compute distance from initial pointer down
     * and update value accordingly
     */
    function handlePointerMove(e: PointerEvent) {
      if (!isDraggingRef.current) return;

      const deltaX = e.clientX - initialXRef.current;
      const distance = Math.abs(deltaX);

      // Only start drag mode if we've moved beyond the threshold
      if (distance >= dragThreshold) {
        hasExceededThresholdRef.current = true;
        setIsDragging(true);
        // Set global cursor and disable text selection during dragging
        document.body.style.cursor = 'ew-resize';
        document.body.style.userSelect = 'none';
        document.body.style.setProperty('-webkit-user-select', 'none');
        document.body.style.setProperty('-ms-user-select', 'none');

        // Apply sensitivity factor to make scrubbing slower
        let newValue = initialValueRef.current + deltaX * step * scrubSensitivity;
        // Clamp and quantize the value
        const effectiveStep = step === 0 ? 1 : step;
        const quantized = Math.round(newValue / effectiveStep) * effectiveStep;
        const minValue = min ?? -Infinity;
        const maxValue = max ?? Infinity;
        const clamped = Math.max(minValue, Math.min(quantized, maxValue));
        const stepQuantized = round(clamped, stepDecimals);
        // Apply final decimal places if specified
        newValue = decimalPlaces !== undefined ? round(stepQuantized, decimalPlaces) : stepQuantized;

        // Store the final drag value so we can use it when the drag ends
        finalDragValueRef.current = newValue;
        setInternalValue(newValue);
        setDisplayValue(formatDisplayValue(newValue, decimalPlaces));
        onChange(newValue, UndoRedoActionType.Silent); // Silent during dragging
      }
    }

    /**
     * On pointer up: stop dragging and potentially enter editing mode
     */
    function handlePointerUp() {
      const wasDragging = hasExceededThresholdRef.current;
      isDraggingRef.current = false;
      hasExceededThresholdRef.current = false;
      setIsDragging(false);
      // Reset global cursor and re-enable text selection
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.removeProperty('-webkit-user-select');
      document.body.style.removeProperty('-ms-user-select');
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);

      // If we were dragging, create proper undo operation
      if (wasDragging) {
        // Use the final drag value that was captured during the last pointer move
        const desiredNewValue = finalDragValueRef.current;
        onChange(previousValue, UndoRedoActionType.Silent);
        onChange(desiredNewValue, UndoRedoActionType.Primary);
        // Ensure editing mode is off and blur the input after dragging
        setIsEditing(false);

        // Prevent any focus attempts for a short time after drag
        preventFocusAfterDragRef.current = true;
        setTimeout(() => {
          preventFocusAfterDragRef.current = false;
        }, 100);

        inputRef.current?.blur();
      } else if (!disabled) {
        setIsEditing(true);
        // Focus the input for editing
        setTimeout(() => {
          inputRef.current?.focus();
          // If we were dragging (scrubbing), select all text
          // If we weren't dragging (just clicking), don't select
          if (wasDragging) {
            inputRef.current?.select();
          }
        }, 0);
      }
    }

    /**
 * When user types in the input (only allowed in editing mode)
 */
    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
      // Only allow input changes when in editing mode
      if (!isEditing) return;

      let inputVal = e.target.value;

      // If first character is a decimal separator, prepend "0"
      if (inputVal === '.' || inputVal === ',') {
        inputVal = '0' + inputVal;
      }

      // Validate that input only contains valid numeric characters
      // Allow: digits, optional minus at start, single period or comma as decimal separator
      const isValidNumeric = (str: string): boolean => {
        // Empty string is valid (intermediate state)
        if (str === '') return true;

        // Just a minus sign is valid (intermediate state for negative numbers)
        if (str === '-') return true;

        // Minus followed by decimal separator (intermediate state)
        if (str === '-.' || str === '-,') return true;

        // Check pattern: optional minus, followed by digits and optional decimal part
        return /^-?\d*[.,]?\d*$/.test(str) && !/^-?[.,]$/.test(str) && !/^-?[.,][.,]/.test(str);
      };

      if (!isValidNumeric(inputVal)) {
        return; // Don't update if input contains non-numeric characters
      }

      setDisplayValue(inputVal);

      // Consider input "empty" only if it's truly empty
      const isEffectivelyEmpty = inputVal === '';
      if (isEffectivelyEmpty) {
        setIsEmpty(true);
        return;
      }

      setIsEmpty(false);

      // Don't update the actual value while typing - only update display
      // The actual value will be updated on blur, Enter, or other commit events
      // This prevents interference while the user is in the middle of typing
    }

    /**
     * When user focuses the input
     */
    function handleInputFocus(e: React.FocusEvent<HTMLInputElement>) {
      // Prevent focus if we just finished dragging
      if (preventFocusAfterDragRef.current) {
        e.currentTarget.blur();
        return;
      }
    }

    /**
     * When user leaves the input (blur)
     */
    function handleInputBlur() {
      // Exit editing mode
      setIsEditing(false);

      // Clear text selection when losing focus
      if (inputRef.current) {
        // Try multiple methods to clear selection
        inputRef.current.setSelectionRange(0, 0);

        // Also clear window selection
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
        }

        // Force it with setTimeout after blur completes
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(0, 0);
          }
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
          }
        }, 0);
      }

      // Check if input is effectively incomplete (empty or just incomplete fragments)
      const normalizedDisplay = normalizeInput(displayValue);
      const isIncomplete = isEmpty ||
        normalizedDisplay === '' ||
        normalizedDisplay === '-' ||
        normalizedDisplay === '.';

      if (isIncomplete) {
        // Reset to current value if empty or incomplete
        setDisplayValue(formatDisplayValue(internalValue, decimalPlaces));
        setIsEmpty(false);
        return;
      }

      // Commit the current display value
      commitValue(displayValue);

      // If commit failed (invalid input), reset to current internal value
      if (isNaN(parseFloat(normalizedDisplay))) {
        setDisplayValue(formatDisplayValue(internalValue, decimalPlaces));
      }
    }

    /**
     * Handle key down events
     */
    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      if (e.key === 'Escape') {
        // Reset to current internal value on Escape and exit editing mode
        setDisplayValue(formatDisplayValue(internalValue, decimalPlaces));
        setIsEmpty(false);
        setIsEditing(false);
        e.currentTarget.blur();
      } else if (e.key === 'Enter') {
        // Check if input is effectively incomplete
        const normalizedDisplay = normalizeInput(displayValue);
        const isIncomplete = isEmpty ||
          normalizedDisplay === '' ||
          normalizedDisplay === '-' ||
          normalizedDisplay === '.';

        if (isIncomplete) {
          // Reset to previous value when Enter is pressed on incomplete input
          setInternalValue(previousValue);
          setDisplayValue(formatDisplayValue(previousValue, decimalPlaces));
          setIsEmpty(false);
          onChange(previousValue);
        } else {
          // Commit the current display value on Enter
          commitValue(displayValue);
        }
        setIsEditing(false);
        e.currentTarget.blur();
      }
    }

    /**
     * Sync internalValue with external `value` if it changes
     */
    React.useEffect(() => {
      // Handle null/undefined values
      const safeValue = value ?? 0;

      // Only sync if the external value actually changed and we're not currently editing or dragging
      if (safeValue !== internalValue && !isDragging && !isEditing) {
        // Only update previousValue if we're not currently in a drag operation
        // During dragging, we want to preserve the value that was set at drag start
        if (!isDraggingRef.current) {
          setPreviousValue(internalValue); // Store current value as previous before updating
        }
        setInternalValue(safeValue);
        // Only update display value if we're not in the middle of typing (isEmpty state)
        if (!isEmpty) {
          setDisplayValue(formatDisplayValue(safeValue, decimalPlaces));
        }
      }
    }, [value, internalValue, isDragging, isEditing, isEmpty, decimalPlaces]);

    // If no prefix or suffix, use the simpler structure
    if (!prefixLabel && !suffixLabel) {
      return (
        <div className={cn('relative group flex items-center', isDragging && 'select-none')}>
          <Input
            ref={inputRef}
            type="text"
            /**
             * Hide the default spinners in Chrome/Edge/Safari
             */
            className={cn(
              `[appearance:textfield]
               [&::-webkit-inner-spin-button]:appearance-none
               [&::-webkit-outer-spin-button]:appearance-none
               pr-2 pb-1.25`, // somehow it's not vertically centered without pb
              !disabled && !isEditing && 'hover:cursor-ew-resize active:none',
              isDragging && 'select-none',
              isEditing && 'cursor-text',
              className
            )}
            value={isEmpty ? '' : displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            onPointerDown={!isEditing ? handlePointerDown : undefined}
            draggable={false}
            onDragStart={handleNativeDragStart}
            disabled={disabled}
            readOnly={!isEditing}
            {...rest}
          />

          <div
            className={cn(
              'absolute inset-y-0 right-0 flex items-center px-2 select-none',
              !disabled && !isEditing && 'cursor-ew-resize',
              isEditing && 'pointer-events-none'
            )}
            onPointerDown={!isEditing ? handlePointerDown : undefined}
          >
          </div>
        </div>
      );
    }

    // With prefix/suffix, we need to handle the dragging differently
    return (
      <div className={cn('relative group flex items-center w-full', isDragging && 'select-none')}>
        {prefixLabel && (
          <div
            className={cn(
              'absolute left-2 z-10 flex items-center select-none',
              !disabled && 'cursor-ew-resize hover:text-primary',
            )}
            onPointerDown={handlePointerDown}
          >
            <span className="text-xs text-muted-foreground font-medium">
              {prefixLabel}
            </span>
          </div>
        )}

        <Input
          ref={inputRef}
          type="text"
          prefixLabel={prefixLabel}
          suffixLabel={suffixLabel}
          prefixPadding={prefixPadding}
          suffixPadding={suffixPadding}
          /**
           * Hide the default spinners in Chrome/Edge/Safari
           */
          className={cn(
            `[appearance:textfield]
             [&::-webkit-inner-spin-button]:appearance-none
             [&::-webkit-outer-spin-button]:appearance-none
             pr-2 pb-1.25`, // somehow it's not vertically centered without pb
            !disabled && !isEditing && 'hover:cursor-ew-resize active:none',
            isDragging && 'select-none',
            isEditing && 'cursor-text',
            className
          )}
          value={isEmpty ? '' : displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onPointerDown={!isEditing ? handlePointerDown : undefined}
          draggable={false}
          onDragStart={handleNativeDragStart}
          disabled={disabled}
          readOnly={!isEditing}
          {...rest}
        />

        {suffixLabel && (
          <div
            className={cn(
              'absolute right-2 z-10 flex items-center select-none',
              !disabled && 'cursor-ew-resize hover:text-primary',
            )}
            onPointerDown={handlePointerDown}
          >
            <span className="text-xs text-muted-foreground font-medium">
              {suffixLabel}
            </span>
          </div>
        )}

        <div
          className={cn(
            'absolute inset-y-0 right-0 flex items-center px-2 select-none',
            !disabled && !isEditing && 'cursor-ew-resize',
            isEditing && 'pointer-events-none'
          )}
          onPointerDown={!isEditing ? handlePointerDown : undefined}
        >
        </div>
      </div>
    );
  }
);

InputNumber.displayName = 'NumericScrubber';
